<template>
  <div id="mobileNavigation" ref="offcanvasElement" class="offcanvas offcanvas-start mobile-navigation" tabindex="-1" aria-labelledby="mobileNavigationLabel">
    <div class="offcanvas-header mobile-navigation__header">
      <div class="mobile-navigation__brand">
        <img :src="logo" alt="" aria-hidden="true">
        <div>
          <h2 id="mobileNavigationLabel">DocBrain</h2>
          <span>{{ company || 'Gestión inteligente' }}</span>
        </div>
      </div>
      <button type="button" class="btn-close btn-close-white" data-bs-dismiss="offcanvas" aria-label="Cerrar menú"></button>
    </div>

    <div class="mobile-navigation__identity">
      <span class="mobile-navigation__avatar" aria-hidden="true"><i class="fa-solid fa-user"></i></span>
      <div>
        <strong>{{ user?.full_name || 'Usuario' }}</strong>
        <span>{{ user?.email }}</span>
      </div>
    </div>

    <div class="offcanvas-body mobile-navigation__body">
      <nav aria-label="Navegación móvil">
        <section v-for="(group, groupIndex) in groups" :key="group.title || 'principal'" class="mobile-navigation__group">
          <button
            v-if="group.title"
            class="mobile-navigation__group-toggle"
            type="button"
            :disabled="group.items.length === 0"
            :aria-expanded="openGroups.has(groupIndex)"
            @click="toggleGroup(groupIndex)"
          >
            <i v-if="group.icon" :class="group.icon" aria-hidden="true"></i>
            <span>{{ group.title }}</span>
            <i
              v-if="group.items.length > 0"
              class="fa-solid fa-chevron-down mobile-navigation__group-chevron"
              :class="{ 'mobile-navigation__group-chevron--open': openGroups.has(groupIndex) }"
              aria-hidden="true"
            ></i>
          </button>
          <div v-show="!group.title || openGroups.has(groupIndex)" class="mobile-navigation__subnav">
            <router-link
              v-for="item in group.items"
              :key="item.route"
              class="mobile-navigation__link"
              :to="item.route"
              :aria-current="isActive(item.route) ? 'page' : undefined"
              @click="close"
            >
              <i :class="item.icon" aria-hidden="true"></i>
              <span>{{ item.label }}</span>
            </router-link>
          </div>
        </section>
      </nav>
    </div>
    <div class="mobile-navigation__footer">
      <router-link to="/perfil" class="mobile-navigation__footer-link" @click="close">
        <i class="fa-solid fa-user-gear" aria-hidden="true"></i>
        Mi perfil
      </router-link>
      <button type="button" class="mobile-navigation__footer-link mobile-navigation__footer-link--danger" @click="$emit('logout')">
        <i class="fa-solid fa-right-from-bracket" aria-hidden="true"></i>
        Cerrar sesión
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Offcanvas } from 'bootstrap'
import { useRoute } from 'vue-router'
import logo from '@/assets/docbrain-logo-transparent.webp'
import type { NavigationGroup } from '@/config/navigation'
import type { AuthUser } from '@/store'

const props = defineProps<{ groups: NavigationGroup[], user: AuthUser | null }>()
defineEmits<{ (event: 'logout'): void }>()
const route = useRoute()
const offcanvasElement = ref<HTMLElement | null>(null)
const openGroups = ref(new Set<number>())
let instance: Offcanvas | null = null

const company = computed(() =>
  props.user?.organization?.trim() ||
  props.user?.receipt_profile?.business_name?.trim() ||
  ''
)
const routeMatches = (path: string) =>
  route.path === path || (path !== '/dashboard' && route.path.startsWith(`${path}/`))
const activeMenuRoute = computed(() =>
  props.groups
    .flatMap((group) => group.items)
    .filter((item) => routeMatches(item.route))
    .sort((first, second) => second.route.length - first.route.length)[0]?.route
)
const isActive = (path: string) => activeMenuRoute.value === path
const close = () => instance?.hide()
const openActiveGroups = () => {
  props.groups.forEach((group, index) => {
    if (group.items.some((item) => isActive(item.route))) openGroups.value.add(index)
  })
}
const toggleGroup = (index: number) => {
  if (props.groups[index].items.length === 0) return
  const next = new Set(openGroups.value)
  next.has(index) ? next.delete(index) : next.add(index)
  openGroups.value = next
}

onMounted(() => {
  if (offcanvasElement.value) instance = Offcanvas.getOrCreateInstance(offcanvasElement.value)
  openActiveGroups()
})
watch(() => route.path, openActiveGroups)
onBeforeUnmount(() => {
  instance?.dispose()
  instance = null
})
</script>
